"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { budgetTabs, musicTabs, randoTabs, lightTabs, voitureTabs,
  veloTabs, disposTabs, santeTabs } from "@/app/lib/navigation";
import { useAccessibleModules } from "@/lib/auth/use-user-plan";
import type { ModuleFlags } from "./app-shell";

/**
 * REFONTE v2 — Command Palette (⌘K / Ctrl+K).
 *
 * Navigation instantanée power-user : toutes les pages des modules
 * accessibles (plan + flags Réglages), filtrées en tapant, navigables
 * au clavier. Montée une fois dans AppShell ; ne rend rien tant
 * qu'elle est fermée.
 *
 * Ouverture :
 *   - ⌘K (mac) / Ctrl+K (autres)
 *   - event `gestic:open-palette` (bouton recherche du sidebar)
 *
 * Périmètre v1 : navigation uniquement (pas d'actions destructives).
 */

interface PaletteItem {
  id: string;
  label: string;
  group: string;
  href: string;
  icon: string; // SVG path d (viewBox 24)
}

const MODULE_META: Record<string, { label: string; icon: string }> = {
  budget: { label: "Budget", icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" },
  music: { label: "Music", icon: "M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" },
  famille: { label: "Famille", icon: "M12 2v4m-4-4v4m8-4v4M3 10h18M5 10v10a2 2 0 002 2h10a2 2 0 002-2V10M9 14a3 3 0 116 0" },
  rando: { label: "Rando", icon: "M3 21l4-7 5 4 4-6 5 9M3 21h18" },
  light: { label: "Light", icon: "M20.24 12.24a6 6 0 00-8.49-8.49L5 10.5V19h8.5l6.74-6.76zM16 8L2 22M17.5 15H9" },
  voiture: { label: "Voiture", icon: "M5 11l1.6-4.8A2 2 0 018.5 5h7a2 2 0 011.9 1.2L19 11m-14 0h14a2 2 0 012 2v3a1 1 0 01-1 1h-1m-15-6a2 2 0 00-2 2v3a1 1 0 001 1h1m2 0h8M7 17a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm13 0a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" },
  dispos: { label: "Dispos", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
  velo: { label: "Vélo", icon: "M5 17.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7zm14 0a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM5 14l4-8h5m-3 8l4-8m0 0h3m-3 8h5" },
  sante: { label: "Santé", icon: "M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" },
};

function normalize(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

export function CommandPalette({
  activeModule,
  moduleFlags,
}: {
  activeModule: "budget" | "music" | "famille" | "rando" | "light" | "voiture" | "velo" | "dispos" | "sante";
  moduleFlags: ModuleFlags;
}) {
  const router = useRouter();
  const allowed = useAccessibleModules();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Ouverture = reset query/sélection (état remis ICI, pas dans un effet,
  // pour éviter les renders en cascade — règle react-hooks/set-state-in-effect).
  const openPalette = useCallback(() => {
    setQuery("");
    setSelected(0);
    setOpen(true);
  }, []);

  // ── Raccourci clavier global + event d'ouverture externe ──
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (open) setOpen(false);
        else openPalette();
      }
    }
    function onOpenEvent() { openPalette(); }
    window.addEventListener("keydown", onKey);
    window.addEventListener("gestic:open-palette", onOpenEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("gestic:open-palette", onOpenEvent);
    };
  }, [open, openPalette]);

  // Focus de l'input après le paint du portal (DOM = système externe → OK en effet).
  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  // ── Construction des items (pages du module actif d'abord) ──
  const items = useMemo<PaletteItem[]>(() => {
    const out: PaletteItem[] = [];

    const pushBudget = (group: string) => {
      for (const tab of budgetTabs) {
        if (tab.href === "/budget/intermittence" && !moduleFlags.intermittenceEnabled) continue;
        if (tab.href === "/budget/portefeuille" && !moduleFlags.portefeuilleEnabled) continue;
        if (tab.optionalModule === "groupeAmis" && !moduleFlags.groupeAmisEnabled) continue;
        const label = tab.optionalModule === "groupeAmis" ? moduleFlags.groupeAmisLabel : tab.label;
        out.push({ id: tab.href, label, group, href: tab.href, icon: tab.icon });
      }
    };
    const pushMusic = (group: string) => {
      for (const tab of musicTabs) out.push({ id: tab.href, label: tab.label, group, href: tab.href, icon: tab.icon });
    };
    const pushRando = (group: string) => {
      for (const tab of randoTabs) out.push({ id: tab.href, label: tab.label, group, href: tab.href, icon: tab.icon });
    };
    const pushFamille = (group: string) => {
      out.push({ id: "/famille", label: "L'arbre généalogique", group, href: "/famille", icon: MODULE_META.famille.icon });
    };
    const pushLight = (group: string) => {
      for (const tab of lightTabs) out.push({ id: tab.href, label: `Light — ${tab.label}`, group, href: tab.href, icon: tab.icon });
    };
    const pushVoiture = (group: string) => {
      for (const tab of voitureTabs) out.push({ id: tab.href, label: tab.label, group, href: tab.href, icon: tab.icon });
    };
    const pushVelo = (group: string) => {
      for (const tab of veloTabs) out.push({ id: tab.href, label: tab.label, group, href: tab.href, icon: tab.icon });
      for (const tab of disposTabs) out.push({ id: tab.href, label: tab.label, group, href: tab.href, icon: tab.icon });
    };
    const pushSante = (group: string) => {
      for (const tab of santeTabs) out.push({ id: tab.href, label: tab.label, group, href: tab.href, icon: tab.icon });
    };

    // Pages du module actif.
    const activeGroup = `Aller à — ${MODULE_META[activeModule].label}`;
    if (activeModule === "budget" && allowed.has("budget")) pushBudget(activeGroup);
    if (activeModule === "music" && allowed.has("music")) pushMusic(activeGroup);
    if (activeModule === "rando" && allowed.has("rando")) pushRando(activeGroup);
    if (activeModule === "famille" && allowed.has("famille")) pushFamille(activeGroup);
    if (activeModule === "light" && allowed.has("light")) pushLight(activeGroup);
    if (activeModule === "voiture" && allowed.has("voiture")) pushVoiture(activeGroup);
    if (activeModule === "velo" && allowed.has("velo")) pushVelo(activeGroup);
    if (activeModule === "sante" && allowed.has("sante")) pushSante(activeGroup);

    // Autres modules (entrée racine + leurs pages, après).
    for (const mod of ["budget", "music", "famille", "rando", "light", "voiture", "velo", "sante"] as const) {
      if (mod === activeModule || !allowed.has(mod)) continue;
      const group = "Autres modules";
      if (mod === "budget") pushBudget(group);
      else if (mod === "music") pushMusic(group);
      else if (mod === "rando") pushRando(group);
      else if (mod === "light") pushLight(group);
      else if (mod === "voiture") pushVoiture(group);
      else if (mod === "velo") pushVelo(group);
      else if (mod === "sante") pushSante(group);
      else pushFamille(group);
    }
    return out;
  }, [activeModule, moduleFlags, allowed]);

  // ── Filtrage (insensible accents/casse, préfixe > inclusion) ──
  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return items;
    const starts: PaletteItem[] = [];
    const includes: PaletteItem[] = [];
    for (const it of items) {
      const l = normalize(it.label);
      if (l.startsWith(q)) starts.push(it);
      else if (l.includes(q)) includes.push(it);
    }
    return [...starts, ...includes];
  }, [items, query]);

  // Sélection effective = clamp DÉRIVÉ au render (pas de setState en effet).
  const sel = Math.max(0, Math.min(selected, filtered.length - 1));

  const go = useCallback((item: PaletteItem | undefined) => {
    if (!item) return;
    setOpen(false);
    router.push(item.href);
  }, [router]);

  function onInputKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected(Math.min(sel + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected(Math.max(sel - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(filtered[sel]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  // Garde l'item sélectionné visible.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${sel}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [sel]);

  if (typeof window === "undefined" || !open) return null;

  let lastGroup: string | null = null;

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-start justify-center p-4 pt-[12vh]" onClick={() => setOpen(false)}>
      <div className="absolute inset-0 modal-backdrop" aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Palette de commandes"
        onClick={(e) => e.stopPropagation()}
        className="relative modal-panel rounded-2xl w-full max-w-[560px] overflow-hidden flex flex-col max-h-[60vh] animate-lg-scale-spring"
      >
        {/* Champ de recherche */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border-default/40 shrink-0">
          <svg className="w-4 h-4 text-text-muted shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
          </svg>
          <input type="search"
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(0); }}
            onKeyDown={onInputKey}
            placeholder="Aller à une page…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-text-muted"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:block text-[10px] font-medium text-text-muted bg-surface-secondary border border-border-default/50 rounded-md px-1.5 py-0.5">esc</kbd>
        </div>

        {/* Résultats */}
        <div ref={listRef} className="flex-1 overflow-y-auto overscroll-contain py-1.5">
          {filtered.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-text-muted">Aucun résultat pour « {query} »</p>
          )}
          {filtered.map((item, idx) => {
            const showGroup = item.group !== lastGroup;
            lastGroup = item.group;
            const active = idx === selected;
            return (
              <div key={`${item.group}-${item.id}`}>
                {showGroup && (
                  <p className="px-4 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted/70 select-none">
                    {item.group}
                  </p>
                )}
                <button
                  type="button"
                  data-idx={idx}
                  onClick={() => go(item)}
                  onMouseMove={() => setSelected(idx)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors duration-75 ${
                    active ? "bg-forest/10 text-foreground" : "text-text-secondary"
                  }`}
                >
                  <svg className={`w-4 h-4 shrink-0 ${active ? "text-forest" : "text-text-muted"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                  </svg>
                  <span className="truncate">{item.label}</span>
                  {active && (
                    <svg className="w-3.5 h-3.5 ml-auto text-text-muted shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Hints clavier */}
        <div className="flex items-center gap-3 px-4 py-2 border-t border-border-default/40 text-[10px] text-text-muted shrink-0 select-none">
          <span><kbd className="font-semibold">↑↓</kbd> naviguer</span>
          <span><kbd className="font-semibold">↵</kbd> ouvrir</span>
          <span className="ml-auto"><kbd className="font-semibold">⌘K</kbd> ouvrir/fermer</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
